<template>
  <div>
    <div class="mb-6 text-3xl font-light lg:text-5xl">{{ $t('inquiryForm.stepTwo.title') }}</div>
    <div class="mb-[6px] text-base text-neutral-500 lg:mb-3">
      {{ $t('inquiryForm.stepTwo.description') }}
    </div>
    <div class="grid-default lg:!gap-x-4">
      <div class="col-span-6 mb-0">
        <FormField :label="$t('inquiryForm.stepTwo.form.product.label')">
          <Multiselect
            id="product"
            v-model="selectedProduct"
            class="product-multiselect"
            :options="store.products"
            label="product"
            name="product"
            :placeholder="$t('inquiryForm.stepTwo.form.product.placeholder')"
            selectLabel=""
            deselectLabel=""
            selectedLabel=""
            :allow-empty="true"
            @open="isOpen = true"
            @close="isOpen = false"
            @select="optionSelected"
          >
            <template #noResult>{{ $t('inquiryForm.noResults') }}</template>
            <template #singleLabel="{ option }">
              <div class="flex w-fit items-center gap-4 bg-light-gray-200">
                <img
                  class="h-14 w-14"
                  loading="lazy"
                  :src="option.productImage.url"
                  :alt="option.productImage.alt"
                />
                <span class="text-base text-blue-950">{{ option.product }}</span>
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex items-center gap-4">
                <img
                  class="h-14 w-14"
                  loading="lazy"
                  :src="option.productImage.url"
                  :alt="option.productImage.alt"
                />
                <span class="text-base text-blue-950">{{ option.product }}</span>
              </div>
            </template>
            <template #caret>
              <button
                type="button"
                class="absolute right-6 top-1/2 -translate-y-1/2"
                @click="handleDeselect"
              >
                <Icon
                  :name="selectedProduct ? 'close' : 'chevron'"
                  class="transition-transform"
                  :class="isOpen && !selectedProduct ? 'rotate-180' : ''"
                  :size="selectedProduct ? 'md' : 'sm'"
                  color="#314863"
                />
              </button>
            </template>
          </Multiselect>
        </FormField>
      </div>

      <div
        class="grid-default col-span-6 gap-y-6 lg:col-span-12 lg:!gap-x-4 lg:gap-y-8"
        :class="{ 'mb-6 lg:mb-8 ': selectedProduct?.material?.length }"
      >
        <InquiryCheckbox
          v-for="(product, index) in getValuesByLanguage(selectedProduct?.material, locale)"
          v-show="selectedProduct?.material && selectedProduct?.material?.length"
          :id="`ProductMaterial`"
          :key="index"
          v-model="state.ProductMaterial"
          name="ProductMaterial"
          class="col-span-6 sm:col-span-3 lg:col-span-4"
          :value-by="product!"
          :title="product!"
        />
      </div>

      <div v-show="selectedProduct?.customDimensions" class="col-span-6">
        <FormInput
          id="Dimensions"
          v-model="state.Dimensions"
          name="Dimensions"
          type="text"
          :placeholder="$t('inquiryForm.stepTwo.form.dimensions.placeholder')"
        >
          <template #label>
            {{ $t('inquiryForm.stepTwo.form.dimensions.label') }}
            <span class="text-neutral-500"
              >({{ $t('inquiryForm.stepTwo.form.dimensions.labelInfo') }})</span
            >
          </template>
        </FormInput>
      </div>
      <div v-show="selectedProduct?.customMountingHoleSize" class="col-span-6">
        <FormInput
          id="MountingHoleSize"
          v-model="state.MountingHoleSize"
          name="MountingHoleSize"
          type="text"
          :placeholder="$t('inquiryForm.stepTwo.form.mountingHoleSize.placeholder')"
        >
          <template #label
            >{{ $t('inquiryForm.stepTwo.form.mountingHoleSize.label') }}
            <span class="text-neutral-500">(mm)</span></template
          >
        </FormInput>
      </div>

      <div
        v-show="selectedProduct?.discDiameter && selectedProduct?.discDiameter?.length"
        class="col-span-6"
      >
        <FormField>
          <template #label
            >{{ $t('inquiryForm.stepTwo.form.discDiameter.label') }}
            <span class="text-neutral-500">(mm)</span></template
          >
          <Multiselect
            id="DiscDiameter"
            v-model="state.DiscDiameter"
            :options="selectedProduct?.discDiameter ?? []"
            name="DiscDiameter"
            :showLabels="false"
            :placeholder="$t('inquiryForm.stepTwo.form.discDiameter.placeholder')"
          >
            <template #noResult>{{ $t('inquiryForm.noResults') }}</template>
          </Multiselect>
        </FormField>
      </div>

      <div
        v-show="selectedProduct?.mountingHoleSize && selectedProduct.mountingHoleSize.length"
        class="col-span-6"
      >
        <FormField>
          <template #label
            >{{ $t('inquiryForm.stepTwo.form.mountingHoleSize.label') }}
            <span class="text-neutral-500">(mm)</span></template
          >
          <Multiselect
            id="MountingHoleSize"
            v-model="state.MountingHoleSize"
            :options="selectedProduct?.mountingHoleSize ?? []"
            name="MountingHoleSize"
            :showLabels="false"
            :placeholder="$t('inquiryForm.stepTwo.form.mountingHoleSize.placeholder')"
          >
            <template #noResult>{{ $t('inquiryForm.noResults') }}</template>
          </Multiselect>
        </FormField>
      </div>

      <div v-show="selectedProduct?.customGranulation" class="col-span-6">
        <FormInput
          id="Granulation"
          v-model="state.Granulation"
          name="Granulation"
          :label="$t('inquiryForm.stepTwo.form.granulation.label')"
          :placeholder="$t('inquiryForm.stepTwo.form.granulation.placeholder')"
        />
      </div>

      <div
        v-show="selectedProduct?.granulation && selectedProduct?.granulation.length"
        class="col-span-6"
      >
        <FormField :label="$t('inquiryForm.stepTwo.form.granulation.label')">
          <Multiselect
            id="Granulation"
            v-model="state.Granulation"
            :options="selectedProduct?.granulation ?? []"
            name="Granulation"
            :showLabels="false"
            :placeholder="$t('inquiryForm.stepTwo.form.granulation.placeholder')"
          >
            <template #noResult>{{ $t('inquiryForm.noResults') }}</template>
          </Multiselect>
        </FormField>
      </div>

      <div
        v-show="selectedProduct?.grainType && selectedProduct.grainType.length"
        class="col-span-6"
      >
        <FormField>
          <template #label>
            {{ $t('inquiryForm.stepTwo.form.grainType.label') }}
          </template>
          <Multiselect
            id="GrainType"
            v-model="state.GrainType"
            :options="getValuesByLanguage(selectedProduct?.grainType, locale) ?? []"
            name="GrainType"
            :showLabels="false"
            :placeholder="$t('inquiryForm.stepTwo.form.grainType.placeholder')"
          >
            <template #noResult>{{ $t('inquiryForm.noResults') }}</template>
          </Multiselect>
        </FormField>
      </div>

      <div
        v-show="selectedProduct?.surfaceType && selectedProduct?.surfaceType?.length"
        class="col-span-6"
      >
        <FormField :label="$t('inquiryForm.stepTwo.form.surfaceType.label')">
          <Multiselect
            id="SurfaceType"
            v-model="state.SurfaceType"
            :options="getValuesByLanguage(selectedProduct?.surfaceType, locale) ?? []"
            name="SurfaceType"
            :showLabels="false"
            :placeholder="$t('inquiryForm.stepTwo.form.surfaceType.placeholder')"
          >
            <template #noResult>{{ $t('inquiryForm.noResults') }}</template>
          </Multiselect>
        </FormField>
      </div>

      <div
        v-show="selectedProduct?.numberOfHoles && selectedProduct?.numberOfHoles?.length"
        class="col-span-6"
      >
        <FormField :label="$t('inquiryForm.stepTwo.form.numberOfHoles.label')">
          <Multiselect
            id="NumberOfHoles"
            v-model="state.NumberOfHoles"
            :options="getValuesByLanguage(selectedProduct?.numberOfHoles, locale) ?? []"
            name="NumberOfHoles"
            :showLabels="false"
            :placeholder="$t('inquiryForm.stepTwo.form.numberOfHoles.placeholder')"
          >
            <template #noResult>{{ $t('inquiryForm.noResults') }}</template>
          </Multiselect>
        </FormField>
      </div>

      <div
        v-show="selectedProduct?.clampingMethod && selectedProduct?.clampingMethod?.length"
        class="col-span-6"
      >
        <FormField :label="$t('inquiryForm.stepTwo.form.clampingMethod.label')">
          <Multiselect
            id="SurfaceType"
            v-model="state.ClampingMethod"
            :options="getValuesByLanguage(selectedProduct?.clampingMethod, locale) ?? []"
            name="SurfaceType"
            :showLabels="false"
            :placeholder="$t('inquiryForm.stepTwo.form.clampingMethod.placeholder')"
          >
            <template #noResult>{{ $t('inquiryForm.noResults') }}</template>
          </Multiselect>
        </FormField>
      </div>

      <FormInput
        id="Quantity"
        v-model="state.Quantity"
        name="Quantity"
        class-field="col-span-6"
        type="number"
        :placeholder="$t('inquiryForm.stepTwo.form.quantity.placeholder')"
      >
        <template #label>
          {{ $t('inquiryForm.stepTwo.form.quantity.label') }}
          <span class="text-neutral-500"
            >({{ $t('inquiryForm.stepTwo.form.quantity.labelInfo') }})</span
          >
        </template>
      </FormInput>

      <div class="col-span-6 mb-4 lg:col-span-12">
        <div class="mb-[6px] text-base text-neutral-500 lg:mb-3">
          {{ $t('inquiryForm.stepTwo.form.message.title') }}
        </div>
        <FormTextarea
          id="productDetailMessage"
          v-model="state.productDetailMessage"
          name="productDetailMessage"
          :placeholder="$t('inquiryForm.stepTwo.form.message.placeholder')"
          :label="$t('inquiryForm.stepTwo.form.message.label')"
          :rows="10"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// eslint-disable-next-line import/no-named-as-default
import Multiselect from 'vue-multiselect'
import { InquiryProductDetails } from '~/types/inquiry'
import { useInquiryStore } from '~/stores/InquiryStore'
import { ProgramProductRecord } from '~/types/generated'

const store = useInquiryStore()
const { t, locale } = useI18n()
const isOpen = ref(false)

const emit = defineEmits(['update:modelValue'])
const props = defineProps({
  modelValue: {
    type: Object as PropType<InquiryProductDetails>,
    required: true
  }
})

const state = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
})

const selectedProduct = ref(store.products.find(el => el.product === state.value?.product))

function optionSelected(option: ProgramProductRecord) {
  if (state.value) {
    state.value.product = option.product as string
    state.value.category = option.category?.programCategory as string
  }
}

function handleDeselect() {
  state.value.product = t('inquiryForm.stepOne.otherTitle')
  state.value.category = t('inquiryForm.stepOne.otherTitle')
  selectedProduct.value = undefined
}

function getValuesByLanguage(arr?: string[], language: string) {
  // Create an array to store values for the specified language
  const values: string[] = []

  // Loop through the array and extract values for the specified language
  arr?.forEach(item => {
    // Split each item by ',' to separate language pairs
    const pairs = item.split(',')

    // Loop through language pairs and find the value for the specified language
    pairs.forEach(pair => {
      const [key, value] = pair.split(':')
      if (key === language) {
        values.push(value)
      }
    })
  })

  return values
}

onMounted(() => {
  selectedProduct.value = store.products.find(el => el.product === state.value?.product)
})

watch(
  () => props.modelValue,
  () => {
    selectedProduct.value = store.products.find(el => el.product === state.value?.product)
  },
  { deep: true }
)
</script>

<style lang="postcss">
.product-multiselect {
  .multiselect__tags {
    @apply flex min-h-[96px] items-center rounded-3xl border-0 bg-light-gray-200 px-6 py-4 shadow-lg;
  }

  .multiselect__single {
    @apply mr-8 w-full bg-light-gray-200 p-0;
  }

  .multiselect__placeholder {
    @apply m-0 p-0 text-base text-blue-950;
  }
}
</style>
